<script setup>
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useAlert } from 'dashboard/composables';
import CanalProAPI from 'dashboard/api/canalpro';
import CanalProConfirmDialog from '../components/CanalProConfirmDialog.vue';
import CanalProPageState from '../components/CanalProPageState.vue';
import {
  collectionFromResponse,
  errorMessage,
  formatCurrency,
  formatDateTime,
  labelForStatus,
  listingAddress,
  listingImageUrl,
  labelForPublicationType,
  metaFromResponse,
  recordFromResponse,
  statusClass,
} from '../utils/canalproData';

const route = useRoute();
const router = useRouter();
const PER_PAGE_OPTIONS = [20, 50, 100];
const DEFAULT_PER_PAGE = 20;

const accounts = ref([]);
const listings = ref([]);
const listingMeta = ref({
  count: 0,
  total_count: 0,
  page: 1,
  per_page: DEFAULT_PER_PAGE,
  total_pages: 0,
  status_counts: {},
});
const loading = ref(true);
const listingLoading = ref(false);
const error = ref('');
const accountId = ref(route.query.account_id || '');
const accountSearch = ref('');
const searchQuery = ref(route.query.q || '');
const statusFilter = ref(route.query.status || '');
const currentPage = ref(Number(route.query.page) || 1);
const perPage = ref(
  PER_PAGE_OPTIONS.includes(Number(route.query.per_page))
    ? Number(route.query.per_page)
    : DEFAULT_PER_PAGE
);
const actionTarget = ref(null);
const changingId = ref(null);
const syncingId = ref(null);
const lastLiveSyncAt = ref(null);
let liveRefreshTimer;

const selectedAccount = computed(() =>
  accounts.value.find(account => String(account.id) === String(accountId.value))
);
const profileName = computed(
  () =>
    selectedAccount.value?.name ||
    selectedAccount.value?.business_name ||
    'Todos os anúncios'
);
const statusCounts = computed(() => listingMeta.value.status_counts || {});
const activeCount = computed(
  () =>
    Number(statusCounts.value.active || 0) +
    Number(statusCounts.value.published || 0)
);
const draftCount = computed(
  () =>
    Number(statusCounts.value.draft || 0) +
    Number(statusCounts.value.ready_to_publish || 0)
);
const totalCount = computed(() => Number(listingMeta.value.total_count || 0));
const hasFilters = computed(() =>
  Boolean(searchQuery.value || statusFilter.value)
);
const filteredAccounts = computed(() => {
  const term = accountSearch.value.trim().toLocaleLowerCase('pt-BR');
  const matching = !term
    ? accounts.value
    : accounts.value.filter(account =>
        [
          account.name,
          account.business_name,
          account.contract_id,
          account.publisher_id,
        ]
          .filter(Boolean)
          .some(value =>
            String(value).toLocaleLowerCase('pt-BR').includes(term)
          )
      );
  const selected = selectedAccount.value;

  return selected && !matching.some(account => account.id === selected.id)
    ? [selected, ...matching]
    : matching;
});
const paginationLabel = computed(() => {
  if (!totalCount.value) return 'Nenhum anúncio encontrado';

  const start =
    (Number(listingMeta.value.page || 1) - 1) *
      Number(listingMeta.value.per_page || perPage.value) +
    1;
  const end = Math.min(start + listings.value.length - 1, totalCount.value);
  return `${start}–${end} de ${totalCount.value} anúncios`;
});

const listingPrice = listing =>
  listing?.pricing?.price ||
  listing?.pricing_infos?.[0]?.price ||
  listing?.price;

const listingAccountName = listing =>
  listing?.canalpro_account?.name ||
  listing?.canalpro_account?.business_name ||
  accounts.value.find(
    account => String(account.id) === String(listing.canalpro_account_id)
  )?.name ||
  'Conta CanalPro';

const listingQuery = () => {
  const query = {};
  if (accountId.value) query.account_id = accountId.value;
  if (searchQuery.value.trim()) query.q = searchQuery.value.trim();
  if (statusFilter.value) query.status = statusFilter.value;
  if (currentPage.value > 1) query.page = String(currentPage.value);
  if (perPage.value !== DEFAULT_PER_PAGE) {
    query.per_page = String(perPage.value);
  }
  return query;
};

const loadAccounts = async () => {
  const response = await CanalProAPI.syncAccounts();
  accounts.value = collectionFromResponse(response);
  if (accountId.value && !selectedAccount.value) accountId.value = '';
  if (!accountId.value && accounts.value.length) {
    accountId.value = String(accounts.value[0].id);
  }
};

const loadListings = async () => {
  listingLoading.value = true;
  error.value = '';
  try {
    const useLiveProxy =
      Boolean(accountId.value) &&
      !searchQuery.value.trim() &&
      !['draft', 'failed'].includes(statusFilter.value);
    const response = await (useLiveProxy
      ? CanalProAPI.liveListings({
          canalpro_account_id: accountId.value,
          ...(statusFilter.value ? { status: statusFilter.value } : {}),
          page: currentPage.value,
          per_page: perPage.value,
        })
      : CanalProAPI.listings({
          ...(accountId.value ? { canalpro_account_id: accountId.value } : {}),
          ...(searchQuery.value.trim() ? { q: searchQuery.value.trim() } : {}),
          ...(statusFilter.value ? { status: statusFilter.value } : {}),
          page: currentPage.value,
          per_page: perPage.value,
        }));
    listings.value = collectionFromResponse(response);
    listingMeta.value = { ...listingMeta.value, ...metaFromResponse(response) };
    if (metaFromResponse(response).source === 'gandalf') {
      lastLiveSyncAt.value = new Date();
    } else {
      lastLiveSyncAt.value = null;
    }
  } catch (requestError) {
    error.value = errorMessage(
      requestError,
      'Não foi possível carregar os anúncios.'
    );
  } finally {
    listingLoading.value = false;
  }
};

const loadPage = async () => {
  loading.value = true;
  error.value = '';
  try {
    await loadAccounts();
    if (accountId.value && !route.query.account_id) {
      await router.replace({ query: listingQuery() });
    }
    await loadListings();
  } catch (requestError) {
    error.value = errorMessage(
      requestError,
      'Não foi possível carregar o CanalPro.'
    );
  } finally {
    loading.value = false;
  }
};

const replaceRouteAndLoad = async () => {
  await router.replace({ query: listingQuery() });
  await loadListings();
};

const applyFilters = async () => {
  currentPage.value = 1;
  await replaceRouteAndLoad();
};

const changeAccount = async () => {
  accountSearch.value = '';
  await applyFilters();
};

const clearFilters = async () => {
  searchQuery.value = '';
  statusFilter.value = '';
  await applyFilters();
};

const goToPage = async page => {
  if (page < 1 || page > Number(listingMeta.value.total_pages || 1)) return;
  currentPage.value = page;
  await replaceRouteAndLoad();
};

const editListing = listing => {
  router.push({
    name: 'canalpro_listing_edit',
    params: { id: listing.id },
    query: listing.canalpro_account_id
      ? { account_id: listing.canalpro_account_id }
      : {},
  });
};

const createListing = () => {
  router.push({
    name: 'canalpro_listing_new',
    query: accountId.value ? { account_id: accountId.value } : {},
  });
};

const updateListingInList = updated => {
  const index = listings.value.findIndex(listing => listing.id === updated.id);
  if (index >= 0) listings.value.splice(index, 1, updated);
};

const confirmStatusChange = listing => {
  actionTarget.value = listing;
};

const applyStatusChange = async () => {
  const listing = actionTarget.value;
  if (!listing) return;

  changingId.value = listing.id;
  try {
    const active = ['active', 'published'].includes(
      String(listing.status).toLowerCase()
    );
    const response = await (active
      ? CanalProAPI.unpublishListing(listing.id)
      : CanalProAPI.publishListing(listing.id));
    updateListingInList(recordFromResponse(response));
    await loadListings();
    useAlert(active ? 'Anúncio despublicado.' : 'Anúncio publicado.');
  } catch (requestError) {
    useAlert(
      errorMessage(requestError, 'Não foi possível alterar este anúncio.')
    );
  } finally {
    changingId.value = null;
    actionTarget.value = null;
  }
};

const syncListing = async listing => {
  syncingId.value = listing.id;
  try {
    const response = await CanalProAPI.syncListing(listing.id);
    updateListingInList(recordFromResponse(response));
    useAlert('Anúncio sincronizado com a conta selecionada no CanalPro.');
  } catch (requestError) {
    useAlert(
      errorMessage(requestError, 'Não foi possível sincronizar este anúncio.')
    );
  } finally {
    syncingId.value = null;
  }
};

watch(
  () => route.query,
  query => {
    const nextAccountId = query.account_id || '';
    const nextSearchQuery = query.q || '';
    const nextStatus = query.status || '';
    const nextPage = Number(query.page) || 1;
    const nextPerPage = PER_PAGE_OPTIONS.includes(Number(query.per_page))
      ? Number(query.per_page)
      : DEFAULT_PER_PAGE;
    const changed =
      String(nextAccountId) !== String(accountId.value) ||
      nextSearchQuery !== searchQuery.value ||
      nextStatus !== statusFilter.value ||
      nextPage !== currentPage.value ||
      nextPerPage !== perPage.value;

    if (!changed) return;

    accountId.value = nextAccountId;
    searchQuery.value = nextSearchQuery;
    statusFilter.value = nextStatus;
    currentPage.value = nextPage;
    perPage.value = nextPerPage;
    if (!loading.value) loadListings();
  },
  { deep: true }
);

onMounted(() => {
  loadPage();
  liveRefreshTimer = window.setInterval(() => {
    if (accountId.value && !loading.value && !listingLoading.value) {
      loadListings();
    }
  }, 15000);
});

onBeforeUnmount(() => window.clearInterval(liveRefreshTimer));
</script>

<template>
  <!-- eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text -->
  <main
    class="h-full min-h-0 overflow-y-auto overscroll-contain bg-slate-50 pb-10"
  >
    <section class="mx-auto max-w-7xl px-4 py-6 sm:px-6 lg:px-8">
      <CanalProPageState
        v-if="loading || error"
        :loading="loading"
        :error="error"
        @retry="loadPage"
      />

      <template v-else>
        <header
          class="overflow-hidden rounded-2xl bg-slate-950 px-5 py-6 text-white shadow-sm sm:px-7"
        >
          <div
            class="flex flex-col gap-6 lg:flex-row lg:items-center lg:justify-between"
          >
            <div class="flex min-w-0 items-center gap-4">
              <div
                class="flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl bg-gradient-to-br from-amber-400 via-rose-500 to-violet-600 p-0.5"
              >
                <span
                  class="flex h-full w-full items-center justify-center rounded-[0.9rem] bg-slate-900 text-lg font-bold text-white"
                >
                  {{ profileName.slice(0, 1).toUpperCase() }}
                </span>
              </div>
              <div class="min-w-0">
                <p class="text-sm font-medium text-indigo-200">
                  CanalPro · Gandalf
                </p>
                <h1 class="mt-1 truncate text-2xl font-semibold tracking-tight">
                  {{ profileName }}
                </h1>
                <p class="mt-2 max-w-2xl text-sm text-slate-300">
                  Visualize, filtre e mantenha cada imóvel sincronizado com a
                  conta certa.
                </p>
              </div>
            </div>
            <div class="flex flex-wrap items-center gap-3">
              <button
                class="inline-flex items-center justify-center gap-2 rounded-xl border border-white/20 px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-white/10 disabled:opacity-60"
                type="button"
                :disabled="listingLoading"
                @click="loadListings"
              >
                <span
                  class="i-lucide-refresh-cw text-base"
                  :class="listingLoading ? 'animate-spin' : ''"
                />
                Atualizar agora
              </button>
              <button
                class="inline-flex items-center justify-center gap-2 rounded-xl bg-white px-4 py-2.5 text-sm font-semibold text-slate-900 shadow-sm transition hover:bg-indigo-50"
                type="button"
                @click="createListing"
              >
                <span class="i-lucide-square-pen text-base" /> Criar anúncio
              </button>
            </div>
          </div>

          <dl
            class="mt-6 grid grid-cols-3 gap-3 border-t border-white/10 pt-5 sm:max-w-xl"
          >
            <div class="rounded-xl bg-white/8 px-3 py-3">
              <dt class="text-xs font-medium text-slate-300">Resultados</dt>
              <dd class="mt-1 text-xl font-semibold">{{ totalCount }}</dd>
            </div>
            <div class="rounded-xl bg-white/8 px-3 py-3">
              <dt class="text-xs font-medium text-slate-300">Publicados</dt>
              <dd class="mt-1 text-xl font-semibold">{{ activeCount }}</dd>
            </div>
            <div class="rounded-xl bg-white/8 px-3 py-3">
              <dt class="text-xs font-medium text-slate-300">Em preparo</dt>
              <dd class="mt-1 text-xl font-semibold">{{ draftCount }}</dd>
            </div>
          </dl>
          <p v-if="lastLiveSyncAt" class="mt-4 text-xs text-slate-300">
            Dados do CanalPro atualizados às
            {{ formatDateTime(lastLiveSyncAt) }}.
            <template v-if="listingMeta.source === 'gandalf'">
              A página selecionada é renovada automaticamente a cada 15
              segundos.
            </template>
          </p>
        </header>

        <form
          class="mt-6 rounded-2xl border border-slate-200 bg-white p-4 shadow-sm sm:p-5"
          @submit.prevent="applyFilters"
        >
          <div
            class="grid gap-4 lg:grid-cols-[minmax(13rem,0.8fr)_minmax(16rem,1.2fr)_12rem_10rem_auto] lg:items-end"
          >
            <label class="grid gap-1.5 text-sm font-medium text-slate-700">
              Buscar conta
              <div class="relative">
                <span
                  class="i-lucide-building-2 pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
                />
                <input
                  v-model="accountSearch"
                  class="w-full rounded-lg border-slate-300 py-2 pl-9 text-sm focus:border-indigo-500 focus:ring-indigo-500"
                  placeholder="Nome, contrato ou publicador"
                />
              </div>
            </label>
            <label class="grid gap-1.5 text-sm font-medium text-slate-700">
              Conta de publicação
              <select
                v-model="accountId"
                class="rounded-lg border-slate-300 py-2 text-sm focus:border-indigo-500 focus:ring-indigo-500"
                @change="changeAccount"
              >
                <option value="">
                  Todas as contas acessíveis (cache local)
                </option>
                <option
                  v-for="account in filteredAccounts"
                  :key="account.id"
                  :value="account.id"
                >
                  {{
                    account.name || account.business_name || 'Conta CanalPro'
                  }}
                </option>
              </select>
            </label>
            <label class="grid gap-1.5 text-sm font-medium text-slate-700">
              Status
              <select
                v-model="statusFilter"
                class="rounded-lg border-slate-300 py-2 text-sm focus:border-indigo-500 focus:ring-indigo-500"
                @change="applyFilters"
              >
                <option value="">Todos os status</option>
                <option value="active">Publicados</option>
                <option value="draft">Rascunhos</option>
                <option value="inactive">Despublicados</option>
                <option value="failed">Com erro</option>
              </select>
            </label>
            <label class="grid gap-1.5 text-sm font-medium text-slate-700">
              Por página
              <select
                v-model="perPage"
                class="rounded-lg border-slate-300 py-2 text-sm focus:border-indigo-500 focus:ring-indigo-500"
                @change="applyFilters"
              >
                <option
                  v-for="size in PER_PAGE_OPTIONS"
                  :key="size"
                  :value="size"
                >
                  {{ size }} anúncios
                </option>
              </select>
            </label>
            <div class="flex gap-2">
              <button
                class="inline-flex flex-1 items-center justify-center gap-2 rounded-lg bg-indigo-600 px-4 py-2 text-sm font-semibold text-white hover:bg-indigo-700"
                type="submit"
              >
                <span class="i-lucide-search text-base" /> Filtrar
              </button>
              <button
                class="rounded-lg border border-slate-300 px-3 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-50"
                type="button"
                :disabled="!hasFilters"
                aria-label="Limpar filtros"
                @click="clearFilters"
              >
                <span class="i-lucide-x text-base" />
              </button>
            </div>
          </div>
          <label class="mt-4 grid gap-1.5 text-sm font-medium text-slate-700">
            Buscar anúncio
            <div class="relative">
              <span
                class="i-lucide-search pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
              />
              <input
                v-model="searchQuery"
                class="w-full rounded-lg border-slate-300 py-2.5 pl-9 text-sm focus:border-indigo-500 focus:ring-indigo-500"
                placeholder="Título, endereço, tipo de imóvel ou código do anúncio"
              />
            </div>
          </label>
        </form>

        <div class="mt-5 flex flex-wrap items-center justify-between gap-3">
          <p class="text-sm text-slate-600">{{ paginationLabel }}</p>
          <button
            class="inline-flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-200 disabled:opacity-60"
            type="button"
            :disabled="listingLoading"
            @click="loadListings"
          >
            <span
              class="i-lucide-refresh-cw"
              :class="listingLoading ? 'animate-spin' : ''"
            />
            Atualizar
          </button>
        </div>

        <CanalProPageState
          v-if="listingLoading || !listings.length"
          class="mt-2 py-14"
          :loading="listingLoading"
          :empty="!listingLoading && !listings.length"
          :empty-title="
            hasFilters
              ? 'Nenhum anúncio encontrado com estes filtros'
              : 'Ainda não há anúncios para estas contas'
          "
          :empty-description="
            hasFilters
              ? 'Ajuste a busca ou limpe os filtros para ver outros imóveis.'
              : 'Crie um anúncio e escolha a conta CanalPro correta antes de publicar.'
          "
        >
          <template #action>
            <button
              v-if="hasFilters"
              class="rounded-lg border border-slate-300 bg-white px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50"
              type="button"
              @click="clearFilters"
            >
              Limpar filtros
            </button>
            <button
              v-else
              class="rounded-lg bg-indigo-600 px-4 py-2 text-sm font-semibold text-white hover:bg-indigo-700"
              type="button"
              @click="createListing"
            >
              Criar anúncio
            </button>
          </template>
        </CanalProPageState>

        <div v-else class="mt-2 grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
          <article
            v-for="listing in listings"
            :key="listing.id"
            class="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm transition hover:-translate-y-0.5 hover:shadow-md"
          >
            <div class="relative aspect-[4/3] bg-slate-100">
              <img
                v-if="listingImageUrl(listing)"
                :src="listingImageUrl(listing)"
                :alt="listing.title || 'Imóvel anunciado'"
                class="h-full w-full object-cover"
              />
              <div
                v-else
                class="flex h-full flex-col items-center justify-center gap-2 text-slate-400"
              >
                <span class="i-lucide-image-off text-3xl" /><span
                  class="text-sm"
                  >Sem foto de capa</span
                >
              </div>
              <span
                class="absolute left-3 top-3 rounded-full px-2.5 py-1 text-xs font-semibold shadow-sm"
                :class="statusClass(listing.status)"
                >{{ labelForStatus(listing.status) }}</span
              >
            </div>
            <div class="p-4">
              <div class="flex items-start justify-between gap-3">
                <div class="min-w-0">
                  <p class="truncate text-xs font-medium text-indigo-700">
                    {{ listingAccountName(listing) }}
                  </p>
                  <h2
                    class="mt-1 line-clamp-2 text-base font-semibold text-slate-900"
                  >
                    {{ listing.title || 'Imóvel sem título' }}
                  </h2>
                </div>
                <p class="shrink-0 text-sm font-semibold text-slate-900">
                  {{ formatCurrency(listingPrice(listing)) }}
                </p>
              </div>
              <p
                class="mt-3 flex items-center gap-1.5 truncate text-sm text-slate-600"
              >
                <span class="i-lucide-map-pin shrink-0" />
                {{ listingAddress(listing) || 'Endereço em preparo' }}
              </p>
              <p class="mt-2 text-xs font-medium text-slate-500">
                Tipo de anúncio:
                {{ labelForPublicationType(listing.publication_type) }}
              </p>
              <p
                v-if="listing.last_error"
                class="mt-3 rounded-lg bg-rose-50 px-3 py-2 text-xs leading-5 text-rose-700"
              >
                <span class="font-semibold">Falha de sincronização:</span>
                {{ listing.last_error }}
              </p>
              <p v-else class="mt-3 text-xs text-slate-500">
                <span
                  class="i-lucide-cloud-check mr-1 inline-block align-text-bottom"
                />
                {{ formatDateTime(listing.last_synced_at) }}
              </p>
              <div
                class="mt-4 grid grid-cols-3 gap-2 border-t border-slate-100 pt-4"
              >
                <button
                  class="inline-flex items-center justify-center gap-1 rounded-lg border border-slate-300 px-2 py-2 text-xs font-semibold text-slate-700 hover:bg-slate-50"
                  type="button"
                  @click="editListing(listing)"
                >
                  <span class="i-lucide-pencil text-sm" /> Editar
                </button>
                <button
                  class="inline-flex items-center justify-center gap-1 rounded-lg border border-indigo-200 bg-indigo-50 px-2 py-2 text-xs font-semibold text-indigo-700 hover:bg-indigo-100 disabled:opacity-60"
                  type="button"
                  :disabled="!listing.remote_id || syncingId === listing.id"
                  @click="syncListing(listing)"
                >
                  <span
                    class="i-lucide-refresh-cw text-sm"
                    :class="syncingId === listing.id ? 'animate-spin' : ''"
                  />
                  {{
                    syncingId === listing.id ? 'Sincronizando' : 'Sincronizar'
                  }}
                </button>
                <button
                  class="inline-flex items-center justify-center gap-1 rounded-lg bg-slate-900 px-2 py-2 text-xs font-semibold text-white hover:bg-slate-700 disabled:opacity-60"
                  type="button"
                  :disabled="changingId === listing.id"
                  @click="confirmStatusChange(listing)"
                >
                  {{
                    changingId === listing.id
                      ? 'Salvando'
                      : ['active', 'published'].includes(
                            String(listing.status).toLowerCase()
                          )
                        ? 'Despublicar'
                        : 'Publicar'
                  }}
                </button>
              </div>
            </div>
          </article>
        </div>

        <nav
          v-if="Number(listingMeta.total_pages || 0) > 1"
          class="mt-6 flex items-center justify-center gap-3"
          aria-label="Paginação dos anúncios"
        >
          <button
            class="inline-flex items-center gap-1 rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
            type="button"
            :disabled="Number(listingMeta.page) <= 1"
            @click="goToPage(Number(listingMeta.page) - 1)"
          >
            <span class="i-lucide-chevron-left" /> Anterior
          </button>
          <span class="text-sm text-slate-600"
            >Página {{ listingMeta.page }} de
            {{ listingMeta.total_pages }}</span
          >
          <button
            class="inline-flex items-center gap-1 rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
            type="button"
            :disabled="
              Number(listingMeta.page) >= Number(listingMeta.total_pages)
            "
            @click="goToPage(Number(listingMeta.page) + 1)"
          >
            Próxima <span class="i-lucide-chevron-right" />
          </button>
        </nav>
      </template>
    </section>

    <CanalProConfirmDialog
      :open="Boolean(actionTarget)"
      :title="
        ['active', 'published'].includes(
          String(actionTarget?.status).toLowerCase()
        )
          ? 'Despublicar este anúncio?'
          : 'Publicar este anúncio?'
      "
      :description="
        ['active', 'published'].includes(
          String(actionTarget?.status).toLowerCase()
        )
          ? 'O imóvel deixa de aparecer nos portais até ser publicado novamente.'
          : 'O imóvel será enviado para a conta selecionada no CanalPro após a confirmação.'
      "
      :confirm-label="
        ['active', 'published'].includes(
          String(actionTarget?.status).toLowerCase()
        )
          ? 'Despublicar'
          : 'Publicar agora'
      "
      @cancel="actionTarget = null"
      @confirm="applyStatusChange"
    />
  </main>
</template>
